import type { RecommendedActionsFeedItem, FeedCardProps } from "./feedTypes.js";
import { useState, useMemo } from "react";
import { Card } from "./Card.js";
import type { ChatMessageFeedItem } from "./feedTypes.js";
import "./RecommendedActionsCard.css";

/**
 * Generate a textual summary from the execution result
 */
function generateSummary(executionResult: ChatMessageFeedItem | null): string {
  if (!executionResult) return "";

  const config = executionResult.config;

  // If there's table data, show count summary
  if (config.tableData?.rows) {
    const count = config.tableData.rows.length;
    const total = config.tableData.pagination?.total || count;
    if (total > count) {
      return `${count} von ${total} Einträgen angezeigt`;
    }
    return `${count} ${count === 1 ? 'Eintrag' : 'Einträge'} gefunden`;
  }

  // If there's an answer text, use it as summary
  if (config.answer) {
    // Limit to first 150 characters for summary
    const summary = config.answer.length > 150
      ? config.answer.substring(0, 150) + '...'
      : config.answer;
    return summary;
  }

  // Default fallback
  return "Ergebnis";
}

/**
 * AI Card Component
 * Displays a search bar at the top with recommended operations as a filterable list below.
 * Clicking an operation uses its text as the prompt title and executes the operation.
 */
export function RecommendedActionsCard({ item, onAction, chatHistory }: FeedCardProps<RecommendedActionsFeedItem>) {
  const config = item.config || { actions: [], onActionClick: () => { }, executionResult: null, isLoading: false };
  const { actions = [], onActionClick, executionResult, isLoading = false, currentAction } = config;

  // Normalize actions to text strings for display
  const actionTexts = useMemo(() => {
    return actions.map(action => typeof action === 'string' ? action : action.text);
  }, [actions]);

  // Generate summary text from execution result
  const summaryText = useMemo(() => generateSummary(executionResult || null), [executionResult]);

  const [searchText, setSearchText] = useState("");
  const [selectedActionIndex, setSelectedActionIndex] = useState<number | null>(null);

  // Filter actions based on search text - nur zeigen wenn kein executionResult vorhanden
  const filteredActions = useMemo(() => {
    // Wenn bereits ein Ergebnis angezeigt wird, keine Autocomplete anzeigen
    if (executionResult) {
      return [];
    }

    if (!searchText.trim()) {
      return actionTexts.slice(0, 8); // Max 8 Vorschläge
    }

    const searchLower = searchText.toLowerCase();
    return actionTexts.filter(action =>
      action.toLowerCase().includes(searchLower)
    ).slice(0, 8); // Max 8 Vorschläge
  }, [actionTexts, searchText, executionResult]);

  const handleActionClick = (action: string, index: number) => {
    setSelectedActionIndex(index);
    // Copy action text to search input and submit
    setSearchText(action);
    onActionClick(action);

    // Reset selection after animation, but keep text
    setTimeout(() => {
      setSelectedActionIndex(null);
    }, 300);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchText.trim()) {
      // If user presses Enter in search, use search text as custom prompt
      onActionClick(searchText.trim());
      // Text bleibt bestehen
    }
  };

  return (
    <div className="ai-card">
      {/* Summary Section - zeigt Zusammenfassung des Ergebnisses */}
      {summaryText && (
        <div className="ai-card-summary">
          <span className="ai-card-summary-text">{summaryText}</span>
        </div>
      )}

      {/* AI Card Body - zeigt entweder großen Ring, Autocomplete Liste oder Ergebnis */}
      <div className="ai-card-body">
        {isLoading ? (
          // Zeige großen pulsierenden Ring während der Verarbeitung
          <div className="ai-card-loading">
            <div className="ai-card-processing-ring">
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#1E88E5" strokeWidth="2.5" fill="none"></circle>
                <circle cx="12" cy="12" r="6.5" fill="white" opacity="0.7"></circle>
              </svg>
            </div>
            {currentAction && (
              <div className="ai-card-processing-action">
                {currentAction}
              </div>
            )}
          </div>
        ) : executionResult ? (
          // Zeige Ergebnis der Ausführung
          <div className="ai-card-result">
            <Card
              item={executionResult}
              data={null}
              loading={false}
              onAction={onAction}
              onReload={() => { }}
              chatHistory={chatHistory}
            />
          </div>
        ) : filteredActions.length > 1 ? (
          // Zeige Autocomplete Operations List mit Search Bar als erstes Element
          <div className="ai-card-operations-list">
            {/* Search Bar als erstes Element in der Liste */}
            <div className="ai-card-search-bar">
              <input
                type="text"
                className="ai-card-search-input"
                placeholder="Was möchten Sie tun?"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoFocus
              />
            </div>
            {filteredActions.map((action, index) => (
              <button
                key={index}
                className={`ai-card-operation-item ${selectedActionIndex === index ? 'selected' : ''}`}
                onClick={() => handleActionClick(action, index)}
              >
                <span className="ai-card-operation-text">{action}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
